import { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sun, Moon, Monitor } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const ThemeToggle = () => {
  const { darkMode, toggleDarkMode } = useAppStore();

  // State to manage the selected radio button value
  // Initialize from localStorage or derive from darkMode
  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system'>(() => {
    const stored = localStorage.getItem('themePreference');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    // Fallback to current darkMode state if no preference is explicitly set
    return darkMode ? 'dark' : 'light';
  });

  // Effect to update the store's theme when the local preference changes
  useEffect(() => {
    toggleDarkMode(themePreference);
  }, [themePreference, toggleDarkMode]);

  // Effect to keep the local themePreference in sync with the global darkMode state
  // This handles cases where the system preference changes or darkMode is toggled elsewhere
  useEffect(() => {
    const storedPreference = localStorage.getItem('themePreference');
    if (storedPreference === 'system') {
      // If system preference is active, update local state based on actual darkMode
      setThemePreference('system');
    } else {
      // Otherwise, ensure local state matches the global darkMode
      setThemePreference(darkMode ? 'dark' : 'light');
    }
  }, [darkMode]);


  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>
          Customize how the application looks
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <RadioGroup
            value={themePreference}
            onValueChange={(value: 'light' | 'dark' | 'system') => setThemePreference(value)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="light" id="theme-light" />
              <Label htmlFor="theme-light" className="flex items-center gap-2 cursor-pointer">
                <Sun className="h-4 w-4" />
                Light Mode
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem value="dark" id="theme-dark" />
              <Label htmlFor="theme-dark" className="flex items-center gap-2 cursor-pointer">
                <Moon className="h-4 w-4" />
                Dark Mode
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem value="system" id="theme-system" />
              <Label htmlFor="theme-system" className="flex items-center gap-2 cursor-pointer">
                <Monitor className="h-4 w-4" />
                System Preference
              </Label>
            </div>
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
};

export default ThemeToggle;